import React, { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarEquipe,
  criarEquipe,
  desativarEquipe,
  listarEquipes,
} from "../../../services/logistica";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";


function EquipeForm({ inicial, onSave, onCancel, unidadeId }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "",
    lider_nome: inicial?.lider_nome || "",
    lider_telefone: inicial?.lider_telefone || "",
    pessoas_str: (inicial?.pessoas || []).join(", "),
    observacoes: inicial?.observacoes || "",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Nome da equipe *</span>
        <Input value={dados.nome} onChange={(e) => setDados({ ...dados, nome: e.target.value })} maxLength={120} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Líder</span>
        <Input value={dados.lider_nome} onChange={(e) => setDados({ ...dados, lider_nome: e.target.value })} maxLength={120} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Telefone do líder</span>
        <Input value={dados.lider_telefone} onChange={(e) => setDados({ ...dados, lider_telefone: e.target.value })} maxLength={30} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Pessoas</span>
        <Textarea
          rows={2}
          value={dados.pessoas_str}
          onChange={(e) => setDados({ ...dados, pessoas_str: e.target.value })}
          placeholder="João, Maria, Pedro (separe por vírgulas)"
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
        <Textarea rows={2} value={dados.observacoes} onChange={(e) => setDados({ ...dados, observacoes: e.target.value })} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={() => {
            const pessoas = dados.pessoas_str
              .split(",").map((s) => s.trim()).filter(Boolean);
            const payload = {
              nome: dados.nome,
              lider_nome: dados.lider_nome,
              lider_telefone: dados.lider_telefone,
              pessoas,
              observacoes: dados.observacoes,
              unidade: unidadeId,
            };
            return onSave(payload);
          }}
          successMessage="Equipe salva"
        >
          Salvar
        </AsyncButton>
      </div>
    </div>
  );
}


export default function EquipesTab() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["equipe:manage"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarEquipes(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      setItems(list);
      if (list.length > 0 && !unidadeId) setUnidadeId(list[0].unidade);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (unidadeId) return;
    request("/api/unidades").then((d) => {
      const list = Array.isArray(d) ? d : d?.results || [];
      if (list.length > 0) setUnidadeId(list[0].id);
    }).catch(() => {});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") await criarEquipe(request, dados);
    else await atualizarEquipe(request, editando.id, dados);
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (e) => {
    if (!window.confirm(`Desativar equipe "${e.nome}"?`)) return;
    await desativarEquipe(request, e.id);
    await carregar();
  };

  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionTitle>{loading ? "Carregando..." : `${items.length} equipe(s)`}</SectionTitle>
          {podeManage && (
            <SecondaryButton onClick={() => setEditando("novo")}>
              <FiPlus size={14} /> Nova
            </SecondaryButton>
          )}
        </div>
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!loading && items.length === 0 && <EmptyMsg>Nenhuma equipe.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Líder</th>
                  <th>Telefone</th>
                  <th>Pessoas</th>
                  <th>Status</th>
                  {podeManage && <th>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id}>
                    <td><strong>{e.nome}</strong></td>
                    <td>{e.lider_nome || "—"}</td>
                    <td>{e.lider_telefone || "—"}</td>
                    <td>{(e.pessoas || []).join(", ") || "—"}</td>
                    <td>
                      <Pill $tone={e.ativo ? "success" : "neutral"}>
                        {e.ativo ? "Ativa" : "Inativa"}
                      </Pill>
                    </td>
                    {podeManage && (
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <SecondaryButton onClick={() => setEditando(e)} style={{ padding: "4px 8px", fontSize: 12 }}>
                            <FiEdit2 size={12} />
                          </SecondaryButton>
                          {e.ativo && (
                            <SecondaryButton onClick={() => handleExcluir(e)} style={{ padding: "4px 8px", fontSize: 12 }}>
                              <FiTrash2 size={12} />
                            </SecondaryButton>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {editando && (
        <SlideOverPanel
          open
          onClose={() => setEditando(null)}
          title={editando === "novo" ? "Nova equipe" : `Editar ${editando.nome}`}
        >
          <EquipeForm
            inicial={editando === "novo" ? null : editando}
            onSave={handleSalvar}
            onCancel={() => setEditando(null)}
            unidadeId={unidadeId}
          />
        </SlideOverPanel>
      )}
    </>
  );
}
